import React from "react";
import {
  Box,
  Typography,
  Grid,
  Container,
  Paper,
  Stack,
  Divider,
  useTheme,
  Chip,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import SectionWrapper from "../components/SectionWrapper";
import ButtonPrimary from "../components/ButtonPrimary";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import GroupsIcon from "@mui/icons-material/Groups";
import PsychologyIcon from "@mui/icons-material/Psychology";
import SchoolIcon from "@mui/icons-material/School";
import VerifiedIcon from "@mui/icons-material/Verified";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import useCountUp from "../hooks/useCountUp";

// ─── Stat Item ────────────────────────────────────────────────────────────────
const StatItem = ({ label, targetValue, suffix = "", active }) => {
  const theme = useTheme();
  const count = useCountUp(targetValue, 2000, active);
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        textAlign: "center",
        p: { xs: 2, md: 3 },
        borderRadius: "16px",
        background: isDark ? "rgba(212,175,55,0.05)" : "rgba(212,175,55,0.06)",
        border: `1px solid ${isDark ? "rgba(212,175,55,0.12)" : "rgba(212,175,55,0.18)"}`,
        transition: "all 0.3s ease",
        "&:hover": {
          background: isDark ? "rgba(212,175,55,0.1)" : "rgba(212,175,55,0.1)",
          borderColor: "#D4AF37",
          transform: "translateY(-4px)",
        },
      }}
    >
      <Typography
        variant="h3"
        sx={{ color: "#D4AF37", fontWeight: 900, mb: 0.5, lineHeight: 1.1 }}
      >
        {count}
        {suffix}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: theme.palette.text.secondary,
          textTransform: "uppercase",
          letterSpacing: 1.5,
          fontWeight: 700,
          fontSize: "0.7rem",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

// ─── Value Card ───────────────────────────────────────────────────────────────
const ValueCard = ({ icon: Icon, title, description }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        height: "100%",
        bgcolor: theme.palette.background.paper,
        borderRadius: "16px",
        border: `1px solid ${isDark ? "rgba(212,175,55,0.08)" : "rgba(212,175,55,0.15)"}`,
        transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
        "&:hover": {
          transform: "translateY(-12px)",
          borderColor: "#D4AF37",
          boxShadow: isDark
            ? "0 20px 50px rgba(0,0,0,0.5), 0 0 0 1px rgba(212,175,55,0.2)"
            : "0 20px 50px rgba(212,175,55,0.12)",
          background: isDark
            ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, rgba(212,175,55,0.06) 100%)`
            : `linear-gradient(135deg, ${theme.palette.background.paper} 0%, rgba(212,175,55,0.04) 100%)`,
        },
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          background: isDark
            ? "radial-gradient(circle, rgba(212,175,55,0.15) 0%, rgba(212,175,55,0.05) 100%)"
            : "radial-gradient(circle, rgba(212,175,55,0.12) 0%, rgba(212,175,55,0.04) 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 3,
        }}
      >
        <Icon sx={{ fontSize: 32, color: "#D4AF37" }} />
      </Box>
      <Typography
        variant="h5"
        sx={{ mb: 2, fontWeight: 700, color: theme.palette.text.primary }}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        sx={{ lineHeight: 1.9, color: theme.palette.text.secondary }}
      >
        {description}
      </Typography>
    </Paper>
  );
};

// ─── Timeline Item ────────────────────────────────────────────────────────────
const TimelineItem = ({ year, title, description, isLast }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box sx={{ display: "flex", gap: 4, mb: isLast ? 0 : 4 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minWidth: 24,
        }}
      >
        <Box
          sx={{
            width: 18,
            height: 18,
            borderRadius: "50%",
            border: "3px solid #D4AF37",
            bgcolor: isDark ? "#0D0D0D" : "#F5F7FA",
            boxShadow: "0 0 0 4px rgba(212,175,55,0.12)",
            flexShrink: 0,
          }}
        />
        {!isLast && (
          <Box
            sx={{
              width: 2,
              flexGrow: 1,
              background:
                "linear-gradient(to bottom, rgba(212,175,55,0.4), rgba(212,175,55,0.05))",
              my: 1,
            }}
          />
        )}
      </Box>
      <Box sx={{ pb: 4 }}>
        <Chip
          label={year}
          size="small"
          sx={{
            mb: 1,
            bgcolor: "rgba(212,175,55,0.1)",
            color: "#D4AF37",
            fontWeight: 800,
            border: "1px solid rgba(212,175,55,0.3)",
            fontSize: "0.75rem",
          }}
        />
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, mb: 0.5, color: theme.palette.text.primary }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}
        >
          {description}
        </Typography>
      </Box>
    </Box>
  );
};

// ─── About Page ───────────────────────────────────────────────────────────────
const About = () => {
  const theme = useTheme();
  const [isInView, setIsInView] = React.useState(false);
  const statsRef = React.useRef(null);
  const isDark = theme.palette.mode === "dark";

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsInView(true);
      },
      { threshold: 0.1 },
    );
    const el = statsRef.current;
    if (el) observer.observe(el);
    return () => {
      if (el) observer.unobserve(el);
    };
  }, []);

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        color: "text.primary",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ── Hero ── */}
      <Box
        sx={{
          pt: { xs: 8, md: 12 },
          pb: { xs: 6, md: 8 },
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
          background: isDark
            ? `radial-gradient(ellipse at 50% 0%, rgba(212,175,55,0.14) 0%, transparent 65%)`
            : `radial-gradient(ellipse at 50% 0%, rgba(212,175,55,0.1) 0%, transparent 65%)`,
        }}
      >
        {/* decorative blobs */}
        <Box
          sx={{
            position: "absolute",
            top: -100,
            left: "10%",
            width: 400,
            height: 400,
            borderRadius: "50%",
            bgcolor: "rgba(212,175,55,0.04)",
            filter: "blur(80px)",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: "5%",
            width: 300,
            height: 300,
            borderRadius: "50%",
            bgcolor: "rgba(212,175,55,0.06)",
            filter: "blur(60px)",
            pointerEvents: "none",
          }}
        />

        <Container
          maxWidth="xl"
          sx={{ px: { xs: 2, md: 6 }, position: "relative", zIndex: 1 }}
        >
          <Box sx={{ maxWidth: 900, mx: "auto" }}>
            <Chip
              label="A DAINIK BHASKAR INITIATIVE"
              sx={{
                mb: 3,
                bgcolor: "rgba(212,175,55,0.1)",
                color: "#D4AF37",
                fontWeight: 800,
                letterSpacing: 2,
                border: "1px solid rgba(212,175,55,0.25)",
                fontSize: "0.7rem",
              }}
            />
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "2.6rem", md: "4.5rem" },
                fontWeight: 900,
                mb: 3,
                lineHeight: 1.1,
                letterSpacing: -1,
                color: "text.primary",
              }}
            >
              Empowering Society through{" "}
              <Box component="span" sx={{ color: "#D4AF37" }}>
                Dignity
              </Box>{" "}
              &amp;{" "}
              <Box component="span" sx={{ color: "#D4AF37" }}>
                Knowledge.
              </Box>
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 6,
                maxWidth: 780,
                mx: "auto",
                fontWeight: 400,
                px: 2,
                lineHeight: 1.8,
                color: "text.secondary",
              }}
            >
              DB Shiksha, powered by the legacy of the Dainik Bhaskar Group, is
              committed to transforming the educational landscape of India by
              bridging the gap between potential and opportunity.
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={3}
              justifyContent="center"
              sx={{ px: 2 }}
            >
              <ButtonPrimary
                size="large"
                component={RouterLink}
                to="/db-shiksha/programs"
                sx={{ py: 2, px: 5, fontSize: "1.05rem" }}
              >
                Our Educational Programs
              </ButtonPrimary>
              <ButtonPrimary
                variant="outlined"
                size="large"
                component={RouterLink}
                to="/db-shiksha/contact"
                sx={{
                  py: 2,
                  px: 5,
                  fontSize: "1.05rem",
                  color: "text.primary",
                  borderColor: isDark
                    ? "rgba(212,175,55,0.4)"
                    : "rgba(212,175,55,0.5)",
                  "&:hover": { borderColor: "#D4AF37" },
                }}
              >
                Join the Mission
              </ButtonPrimary>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* ── Stats ── */}
      <Box
        ref={statsRef}
        sx={{
          py: 5,
          bgcolor: "background.paper",
          borderTop: `1px solid ${isDark ? "rgba(212,175,55,0.1)" : "rgba(212,175,55,0.12)"}`,
          borderBottom: `1px solid ${isDark ? "rgba(212,175,55,0.1)" : "rgba(212,175,55,0.12)"}`,
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 2, md: 6 } }}>
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={6} md={3}>
              <StatItem
                targetValue={22}
                suffix="+"
                label="States Covered"
                active={isInView}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatItem
                targetValue={2008}
                suffix=""
                label="Foundation Year"
                active={isInView}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatItem
                targetValue={2}
                suffix=" Lakh+"
                label="Beneficiaries"
                active={isInView}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatItem
                targetValue={500}
                suffix="+"
                label="Skill Centres"
                active={isInView}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Core Strategy ── */}
      <SectionWrapper
        title="Our Core Strategy"
        subtitle="How we empower marginalized and remote communities across the nation."
        bgcolor="transparent"
      >
        <Grid container spacing={4} sx={{ mt: 0 }}>
          {[
            {
              icon: PsychologyIcon,
              title: "Skill Development",
              desc: "Providing vocational training that translates directly into financial independence and self-reliance.",
            },
            {
              icon: RocketLaunchIcon,
              title: "Digital Bridging",
              desc: "Ensuring technology-enabled learning reaches Tier II and Tier III cities to close the digital divide.",
            },
            {
              icon: VerifiedIcon,
              title: "Social Dignity",
              desc: "Fostering an inclusive society where every individual can lead a life of responsibility and respect.",
            },
          ].map((item, i) => (
            <Grid item xs={12} md={4} key={i}>
              <Box
                sx={{
                  textAlign: "center",
                  p: 4,
                  bgcolor: "background.paper",
                  borderRadius: "16px",
                  border: `1px solid ${isDark ? "rgba(212,175,55,0.08)" : "rgba(212,175,55,0.12)"}`,
                  height: "100%",
                  transition: "all 0.35s ease",
                  "&:hover": {
                    borderColor: "#D4AF37",
                    transform: "translateY(-8px)",
                    boxShadow: isDark
                      ? "0 20px 40px rgba(0,0,0,0.4)"
                      : "0 20px 40px rgba(212,175,55,0.1)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    bgcolor: isDark
                      ? "rgba(212,175,55,0.08)"
                      : "rgba(212,175,55,0.07)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 3,
                  }}
                >
                  <item.icon sx={{ fontSize: 40, color: "#D4AF37" }} />
                </Box>
                <Typography
                  variant="h5"
                  sx={{ mb: 2, fontWeight: 700, color: "text.primary" }}
                >
                  {item.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ lineHeight: 1.8, color: "text.secondary" }}
                >
                  {item.desc}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </SectionWrapper>

      {/* ── Story Grid ── */}
      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: "background.paper" }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, md: 6 } }}>
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ position: "relative" }}>
                <Box
                  component="img"
                  src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&q=80&w=1000"
                  alt="DB Shiksha team empowering communities"
                  sx={{
                    width: "100%",
                    borderRadius: "16px",
                    boxShadow: isDark
                      ? "0 30px 60px rgba(0,0,0,0.7)"
                      : "0 30px 60px rgba(0,0,0,0.12)",
                    position: "relative",
                    zIndex: 2,
                    filter: isDark ? "brightness(0.88)" : "none",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    bottom: -24,
                    right: -24,
                    width: "100%",
                    height: "100%",
                    border: "3px solid #D4AF37",
                    borderRadius: "16px",
                    zIndex: 1,
                    display: { xs: "none", md: "block" },
                    opacity: isDark ? 0.6 : 0.8,
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography
                variant="overline"
                color="primary"
                sx={{
                  letterSpacing: 3,
                  fontWeight: 700,
                  display: "block",
                  mb: 1,
                }}
              >
                THE LEGACY OF DB GROUP
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  mb: 4,
                  mt: 1,
                  fontWeight: 900,
                  lineHeight: 1.2,
                  color: "text.primary",
                }}
              >
                Empowering India at the{" "}
                <Box component="span" sx={{ color: "#D4AF37" }}>
                  Grassroots.
                </Box>
              </Typography>
              <Typography
                variant="body1"
                paragraph
                sx={{
                  fontSize: "1.1rem",
                  lineHeight: 1.9,
                  color: "text.secondary",
                }}
              >
                Established in 2008, the Dainik Bhaskar Foundation (formerly
                Bhaskar Foundation) serves as the social development arm of the
                Dainik Bhaskar Group. We work in the most challenging parts of
                India to address the needs of deprived sections.
              </Typography>
              <Typography
                variant="body1"
                paragraph
                sx={{
                  fontSize: "1.1rem",
                  lineHeight: 1.9,
                  color: "text.secondary",
                }}
              >
                Our mission is to bring these segments into the mainstream of
                the country's development by imparting literacy, basic life
                skills, and professional vocational training.
              </Typography>
              <Divider
                sx={{
                  my: 3,
                  borderColor: isDark
                    ? "rgba(212,175,55,0.12)"
                    : "rgba(212,175,55,0.2)",
                }}
              />
              <Stack direction="row" spacing={6}>
                <Box>
                  <Typography
                    variant="h4"
                    sx={{ color: "#D4AF37", fontWeight: 900 }}
                  >
                    2 Lakh+
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontWeight: 600 }}
                  >
                    People Trained
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="h4"
                    sx={{ color: "#D4AF37", fontWeight: 900 }}
                  >
                    1.5 Lakh+
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontWeight: 600 }}
                  >
                    Families Supported
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Journey Timeline ── */}
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          bgcolor: "background.default",
          position: "relative",
        }}
      >
        {/* subtle grid bg */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            backgroundImage: isDark
              ? "radial-gradient(circle, rgba(212,175,55,0.03) 1px, transparent 1px)"
              : "radial-gradient(circle, rgba(212,175,55,0.06) 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
        />
        <Container
          maxWidth="xl"
          sx={{ px: { xs: 2, md: 6 }, position: "relative", zIndex: 1 }}
        >
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Typography variant="h2" sx={{ fontWeight: 900, mb: 2 }}>
              Our{" "}
              <Box component="span" sx={{ color: "#D4AF37" }}>
                Journey
              </Box>
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "text.secondary",
                maxWidth: 600,
                mx: "auto",
                fontWeight: 400,
              }}
            >
              Mapping the progress of Bhaskar Foundation's nationwide
              initiatives.
            </Typography>
            <Box
              sx={{
                width: 60,
                height: 4,
                bgcolor: "#D4AF37",
                mx: "auto",
                mt: 3,
                borderRadius: 2,
              }}
            />
          </Box>
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ pr: { md: 4 } }}>
                <TimelineItem
                  year="2008"
                  title="Foundation Established"
                  description="Dainik Bhaskar Group launches its social arm to drive developmental and CSR activities."
                />
                <TimelineItem
                  year="2012"
                  title="Skill Development Focus"
                  description="Intensification of vocational training programs across North and Central India."
                />
                <TimelineItem
                  year="2018"
                  title="Dainik Bhaskar Foundation"
                  description="Rebranded with an expanded vision to touch lives in 22 states across India."
                />
                <TimelineItem
                  year="Present"
                  title="Digital Transformation"
                  description="Integrating technology into primary education and bridging the digital divide for thousands of children."
                  isLast
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ position: "relative" }}>
                <Box
                  sx={{
                    position: "absolute",
                    top: -20,
                    left: -20,
                    width: "100%",
                    height: "100%",
                    border: `2px solid ${isDark ? "rgba(212,175,55,0.12)" : "rgba(212,175,55,0.15)"}`,
                    borderRadius: "16px",
                    zIndex: 0,
                  }}
                />
                <Box
                  component="img"
                  src="https://images.unsplash.com/photo-1524178232363-1fb2b075b655?auto=format&fit=crop&q=80&w=1000"
                  alt="DB Shiksha journey milestones"
                  sx={{
                    width: "100%",
                    borderRadius: "16px",
                    boxShadow: isDark
                      ? "0 20px 50px rgba(0,0,0,0.6)"
                      : "0 20px 50px rgba(0,0,0,0.12)",
                    position: "relative",
                    zIndex: 1,
                    filter: isDark ? "brightness(0.85)" : "none",
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Vision & Mission ── */}
      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: "background.paper" }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, md: 6 } }}>
          <Grid container spacing={5}>
            {[
              {
                icon: AutoStoriesIcon,
                label: "Vision",
                text: '"To Empower society with responsibility and dignity, contributing to building a country of enlightened people with economic reliability."',
                italic: true,
              },
              {
                icon: EmojiEventsIcon,
                label: "Mission",
                text: "To strengthen the primary education ecosystem, inspire students to continue learning, and empower marginalized communities through literacy and life skills.",
                italic: false,
              },
            ].map((item, i) => (
              <Grid item xs={12} md={6} key={i}>
                <Box
                  sx={{
                    p: { xs: 4, md: 5 },
                    height: "100%",
                    borderRadius: "16px",
                    border: `1px solid ${isDark ? "rgba(212,175,55,0.12)" : "rgba(212,175,55,0.2)"}`,
                    background: isDark
                      ? "linear-gradient(135deg, rgba(212,175,55,0.04) 0%, transparent 100%)"
                      : "linear-gradient(135deg, rgba(212,175,55,0.03) 0%, transparent 100%)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      borderColor: "rgba(212,175,55,0.45)",
                      boxShadow: isDark
                        ? "0 10px 30px rgba(0,0,0,0.3)"
                        : "0 10px 30px rgba(212,175,55,0.08)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "10px",
                        bgcolor: "rgba(212,175,55,0.1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <item.icon sx={{ color: "#D4AF37", fontSize: 24 }} />
                    </Box>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 800, color: "#D4AF37" }}
                    >
                      Our {item.label}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "1.1rem",
                      fontStyle: item.italic ? "italic" : "normal",
                      lineHeight: 1.9,
                      color: "text.secondary",
                    }}
                  >
                    {item.text}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Core Values ── */}
      <SectionWrapper
        title="Our Foundation Values"
        subtitle="The core values inherited from the Dainik Bhaskar Group culture."
        bgcolor="transparent"
      >
        <Grid container spacing={4}>
          {[
            {
              icon: GroupsIcon,
              title: "Responsibility",
              description:
                "Owning the transformation of society with dedicated grassroots initiatives.",
            },
            {
              icon: PsychologyIcon,
              title: "Mainstreaming",
              description:
                "Integrating marginalized sections into the national development narrative.",
            },
            {
              icon: TravelExploreIcon,
              title: "Inclusive Reach",
              description:
                "A presence across 22 states, leaving no community behind in the race for progress.",
            },
            {
              icon: SchoolIcon,
              title: "Empowerment",
              description:
                "Unlocking the true potential of India's youth through vocational excellence.",
            },
          ].map((item, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <ValueCard
                icon={item.icon}
                title={item.title}
                description={item.description}
              />
            </Grid>
          ))}
        </Grid>
      </SectionWrapper>

      {/* ── Final CTA ── */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
          background: isDark
            ? `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`
            : `linear-gradient(180deg, ${theme.palette.background.paper} 0%, #EEF0F4 100%)`,
          borderTop: `1px solid ${isDark ? "rgba(212,175,55,0.1)" : "rgba(212,175,55,0.15)"}`,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            bottom: -60,
            left: "50%",
            transform: "translateX(-50%)",
            width: 600,
            height: 300,
            borderRadius: "50%",
            bgcolor: "rgba(212,175,55,0.05)",
            filter: "blur(80px)",
            pointerEvents: "none",
          }}
        />
        <Container
          maxWidth="xl"
          sx={{ px: { xs: 2, md: 6 }, position: "relative", zIndex: 1 }}
        >
          <Box sx={{ maxWidth: 800, mx: "auto" }}>
            <Typography
              variant="h2"
              sx={{ mb: 3, fontWeight: 900, color: "text.primary" }}
            >
              Join the{" "}
              <Box component="span" sx={{ color: "#D4AF37" }}>
                Bhaskar Legacy.
              </Box>
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 6,
                maxWidth: 600,
                mx: "auto",
                color: "text.secondary",
                fontWeight: 400,
              }}
            >
              Be a part of a movement that is redefining self-reliance and
              dignity for millions across India.
            </Typography>
            <ButtonPrimary
              size="large"
              component={RouterLink}
              to="/db-shiksha/programs"
              sx={{
                py: 2.5,
                px: 8,
                fontSize: "1.15rem",
                boxShadow: "0 12px 40px rgba(212,175,55,0.35)",
              }}
            >
              Explore Our Initiatives
            </ButtonPrimary>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default About;
